// services/doctor.service.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import { API_URL } from "../../../app/config";
import { axiosApiInstance } from "../../../app/axios";
import { ErrorToast, SuccessToast } from "../../../components/toast/Toasts";
import { IDoctorDetails } from "../doctorModal";

// ─── DOCTOR CRUD ────────────────────────────────────────────────────────────

export const getAllDoctors = createAsyncThunk(
  "Doctor/FindAllDoctor",
  async (formdata: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/FindAllDoctor`,
        formdata,
      );
      return {
        Data: response.data?.Data || [],
        TotalCount: response.data?.TotalCount || 0,
      };
    } catch (error: any) {
      console.error("Error Fetching Doctors:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const createDoctor = createAsyncThunk(
  "Doctor/InsertDoctor",
  async (doctorData: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/InsertDoctor`,
        doctorData,
      );
      SuccessToast(response.data?.Details || "Doctor created successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error creating Doctor:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const updateDoctor = createAsyncThunk(
  "Doctor/UpdateDoctor",
  async (doctorData: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/UpdateDoctor`,
        doctorData,
      );
      SuccessToast(response.data?.Details || "Doctor updated successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error updating Doctor:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const deleteDoctor = createAsyncThunk(
  "Doctor/DeleteDoctor",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/DeleteDoctor`,
        { ID },
      );
      SuccessToast(response.data?.Details || "Doctor deleted successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error deleting Doctor:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getDoctorByID = createAsyncThunk(
  "Doctor/FindByIDDoctor",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/FindByIDDoctor`,
        { ID },
      );
      const result = response.data?.Data;
      return {
        ID: result?.ID,
        DoctorName: result?.DoctorName,
        Speciality: result?.Speciality
          ? result?.Speciality?.split(",")?.map((item: number) => +item)
          : [],
        Experience: result?.Experience,
        IsMedical: result?.IsMedical || false,
        MedicalRegDetails: result?.MedicalRegDetails,
        AboutDoctor: result?.AboutDoctor,
        Address: result?.Address,
        Area: result?.Area,
        CityID: result?.CityID,
        StateID: result?.StateID,
        CountryID: result?.CountryID,
        Fees: result?.Fees,
        Gender: result?.Gender,
        MobileNo: result?.MobileNo,
        EmailID: result?.EmailID,
        HospitalAffiliation: result?.HospitalAffiliation,
        LanguagesSpoken: result?.LanguagesSpoken
          ? result?.LanguagesSpoken?.split(",")?.map((item: string) => item)
          : [],
        MainImage: result?.MainImage,
        RegistrationCertificateImage: result?.RegistrationCertificateImage,
        DegreeCertificateImage: result?.DegreeCertificateImage,
        IsActive: result?.IsActive ? 1 : 2,
        AverageRating: result?.AverageRating || 0,
        ExpDetails: result?.ExpDetails || [],
        DegreeInfos: result?.DegreeInfos || [],
        FAQs: result?.FAQs || [],
        Ratings: result?.Ratings || [],
        Timings: result?.Timings || [],
        ShiftSettings: result?.ShiftSettings || [],
        Images: result?.Images || [],
      } as IDoctorDetails;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error fetching Doctor:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getAllActiveDoctors = createAsyncThunk(
  "Doctor/FindAllActiveDoctor",
  async () => {
    try {
      const response = await axiosApiInstance.get(
        `${API_URL}/Doctor/FindAllActiveDoctor`,
      );
      return response.data?.Data || [];
    } catch (error: any) {
      console.error("Error Fetching Active Doctors:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const activeInactiveDoctor = createAsyncThunk(
  "Doctor/ActiveInActiveDoctor",
  async (formData: { ID: number; IsActive: boolean }) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/ActiveInActiveDoctor`,
        formData,
      );
      SuccessToast(response.data?.Details || "Status updated successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error updating doctor status:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

// ─── EXPERIENCE DETAILS ───────────────────────────────────────────────────────

export const insertDoctorExpDetail = createAsyncThunk(
  "Doctor/InsertDoctorExpDetail",
  async (data: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/InsertDoctorExpDetail`,
        data,
      );
      SuccessToast(response.data?.Details || "Experience added successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const updateDoctorExpDetail = createAsyncThunk(
  "Doctor/UpdateDoctorExpDetail",
  async (data: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/UpdateDoctorExpDetail`,
        data,
      );
      SuccessToast(response.data?.Details || "Experience updated successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const deleteDoctorExpDetail = createAsyncThunk(
  "Doctor/DeleteDoctorExpDetail",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/DeleteDoctorExpDetail`,
        { ID },
      );
      SuccessToast(response.data?.Details || "Experience deleted successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getDoctorExpByDoctorID = createAsyncThunk(
  "Doctor/FindByDoctorIDExp",
  async (DoctorID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/FindByDoctorIDExp`,
        { DoctorID },
      );
      return response.data?.Data || [];
    } catch (error: any) {
      console.error("Error fetching doctor experience:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

// ─── DEGREE INFO ──────────────────────────────────────────────────────────────

export const insertDoctorDegreeInfo = createAsyncThunk(
  "Doctor/InsertDoctorDegreeInfo",
  async (data: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/InsertDoctorDegreeInfo`,
        data,
      );
      SuccessToast(response.data?.Details || "Degree added successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const updateDoctorDegreeInfo = createAsyncThunk(
  "Doctor/UpdateDoctorDegreeInfo",
  async (data: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/UpdateDoctorDegreeInfo`,
        data,
      );
      SuccessToast(response.data?.Details || "Degree updated successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const deleteDoctorDegreeInfo = createAsyncThunk(
  "Doctor/DeleteDoctorDegreeInfo",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/DeleteDoctorDegreeInfo`,
        { ID },
      );
      SuccessToast(response.data?.Details || "Degree deleted successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getDoctorDegreeByDoctorID = createAsyncThunk(
  "Doctor/FindByDoctorIDDegree",
  async (DoctorID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/FindByDoctorIDDegree`,
        { DoctorID },
      );
      return response.data?.Data || [];
    } catch (error: any) {
      console.error("Error fetching doctor degrees:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

// ─── FAQ ──────────────────────────────────────────────────────────────────────

export const insertDoctorFAQ = createAsyncThunk(
  "Doctor/InsertDoctorFAQ",
  async (data: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/InsertDoctorFAQ`,
        data,
      );
      SuccessToast(response.data?.Details || "FAQ added successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const updateDoctorFAQ = createAsyncThunk(
  "Doctor/UpdateDoctorFAQ",
  async (data: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/UpdateDoctorFAQ`,
        data,
      );
      SuccessToast(response.data?.Details || "FAQ updated successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const deleteDoctorFAQ = createAsyncThunk(
  "Doctor/DeleteDoctorFAQ",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/DeleteDoctorFAQ`,
        { ID },
      );
      SuccessToast(response.data?.Details || "FAQ deleted successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getDoctorFAQByDoctorID = createAsyncThunk(
  "Doctor/FindByDoctorIDFAQ",
  async (DoctorID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/FindByDoctorIDFAQ`,
        { DoctorID },
      );
      return response.data?.Data || [];
    } catch (error: any) {
      console.error("Error fetching doctor FAQs:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

// ─── RATINGS ──────────────────────────────────────────────────────────────────

export const insertDoctorRating = createAsyncThunk(
  "Doctor/InsertDoctorRating",
  async (data: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/InsertDoctorRating`,
        data,
      );
      SuccessToast(response.data?.Details || "Rating added successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const updateDoctorRating = createAsyncThunk(
  "Doctor/UpdateDoctorRating",
  async (data: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/UpdateDoctorRating`,
        data,
      );
      SuccessToast(response.data?.Details || "Rating updated successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const deleteDoctorRating = createAsyncThunk(
  "Doctor/DeleteDoctorRating",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/DeleteDoctorRating`,
        { ID },
      );
      SuccessToast(response.data?.Details || "Rating deleted successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getDoctorRatingsByDoctorID = createAsyncThunk(
  "Doctor/FindByDoctorIDRating",
  async (DoctorID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/FindByDoctorIDRating`,
        { DoctorID },
      );
      return response.data?.Data || [];
    } catch (error: any) {
      console.error("Error fetching doctor ratings:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

// ─── TIMINGS ─────────────────────────────────────────────────────────────────

export const insertDoctorTiming = createAsyncThunk(
  "Doctor/InsertDoctorTiming",
  async (data: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/InsertDoctorTiming`,
        data,
      );
      SuccessToast(response.data?.Details || "Timing added successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const updateDoctorTiming = createAsyncThunk(
  "Doctor/UpdateDoctorTiming",
  async (data: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/UpdateDoctorTiming`,
        data,
      );
      SuccessToast(response.data?.Details || "Timing updated successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const deleteDoctorTiming = createAsyncThunk(
  "Doctor/DeleteDoctorTiming",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/DeleteDoctorTiming`,
        { ID },
      );
      SuccessToast(response.data?.Details || "Timing deleted successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getDoctorTimingsByDoctorID = createAsyncThunk(
  "Doctor/FindByDoctorIDTiming",
  async (DoctorID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/FindByDoctorIDTiming`,
        { DoctorID },
      );
      return response.data?.Data || [];
    } catch (error: any) {
      console.error("Error fetching doctor timings:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

// ─── SHIFT SETTINGS ───────────────────────────────────────────────────────────

export const insertDoctorShiftSettings = createAsyncThunk(
  "Doctor/InsertShiftSettings",
  async (data: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/InsertShiftSettings`,
        data,
      );
      SuccessToast(
        response.data?.Details || "Shift settings added successfully",
      );
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const updateDoctorShiftSettings = createAsyncThunk(
  "Doctor/UpdateShiftSettings",
  async (data: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/UpdateShiftSettings`,
        data,
      );
      SuccessToast(
        response.data?.Details || "Shift settings updated successfully",
      );
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const deleteDoctorShiftSettings = createAsyncThunk(
  "Doctor/DeleteShiftSettings",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/DeleteShiftSettings`,
        { ID },
      );
      SuccessToast(
        response.data?.Details || "Shift settings deleted successfully",
      );
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getDoctorShiftSettingsByDoctorID = createAsyncThunk(
  "Doctor/FindByDoctorIDShiftTiming",
  async (DoctorID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/FindByDoctorIDShiftTiming`,
        { DoctorID },
      );
      return response.data?.Data || [];
    } catch (error: any) {
      console.error("Error fetching doctor shift settings:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

// ─── IMAGES ───────────────────────────────────────────────────────────────────

export const uploadDoctorSingleImage = createAsyncThunk(
  "Doctor/DoctorSingleImageUpload",
  async (formData: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/DoctorSingleImageUpload`,
        formData,
        { headers: { "content-type": "multipart/form-data" } },
      );
      SuccessToast(response.data?.Details || "Image uploaded successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const uploadDoctorMultipleImages = createAsyncThunk(
  "Doctor/DoctorMultipleImageUpload",
  async (formData: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/DoctorMultipleImageUpload`,
        formData,
        { headers: { "content-type": "multipart/form-data" } },
      );
      SuccessToast(response.data?.Details || "Images uploaded successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const deleteDoctorImage = createAsyncThunk(
  "Doctor/DeleteDoctorImage",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/DeleteDoctorImage`,
        { ID },
      );
      SuccessToast(response.data?.Details || "Image deleted successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getDoctorImagesByDoctorID = createAsyncThunk(
  "Doctor/FindByDoctorIDImage",
  async (DoctorID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Doctor/FindByDoctorIDImage`,
        { DoctorID },
      );
      return response.data?.Data || [];
    } catch (error: any) {
      console.error("Error fetching doctor images:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);
